import { ref } from 'vue';
import { projectFunctions } from '@/firebase/config';

const STORAGE_KEY = 'lastExploreContext';

/**
 * ดึงข้อมูลการสำรวจล่าสุดจาก localStorage
 * @returns {Object|null}
 */
export function getLastExploreContext() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch (err) {
    console.error('Error reading lastExploreContext:', err);
    return null;
  }
}

/**
 * บันทึกข้อมูลการสำรวจล่าสุดลง localStorage
 * @param {Object} context
 */
export function saveLastExploreContext(context) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...context,
        timestamp: Date.now()
      })
    );
  } catch (err) {
    console.error('Error saving lastExploreContext:', err);
  }
}

/**
 * ล้างข้อมูลการสำรวจล่าสุด
 */
export function clearLastExploreContext() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    console.error('Error clearing lastExploreContext:', err);
  }
}

/**
 * เรียก Cloud Function เพื่อสำรวจแผนที่
 * @param {string} mapName
 * @param {Object} options
 * @param {string} options.mapType - 'location' | 'supernatural'
 * @param {boolean} options.usedVehicle
 * @returns {Promise<Object>}
 */
export async function exploreMapByName(mapName, options = {}) {
  const { mapType = 'location', usedVehicle = false } = options;

  const exploreFunction = projectFunctions.httpsCallable('gameActions');

  if (mapType === 'supernatural') {
    const result = await exploreFunction({
      action: 'exploreSupernatural',
      subAction: 'explore',
      mapName
    });

    if (!result.data.success) {
      throw new Error(result.data.message || 'เกิดข้อผิดพลาดในการสำรวจ');
    }

    return result.data;
  }

  const result = await exploreFunction({
    action: usedVehicle ? 'useVehicleAndExplore' : 'explore',
    mapName
  });

  if (!result.data.success) {
    throw new Error(result.data.message || 'เกิดข้อผิดพลาดในการสำรวจ');
  }

  return result.data;
}

/**
 * ตรวจสอบว่าสามารถใช้ปุ่มสำรวจล่าสุดได้หรือไม่
 * @param {Object} context - จาก getLastExploreContext()
 * @param {Object} characterPrivateData
 * @returns {Object} { canExplore: boolean, reason: string }
 */
export function canQuickExplore(context, characterPrivateData) {
  if (!context || !context.mapName) {
    return { canExplore: false, reason: 'ยังไม่มีแผนที่ล่าสุด' };
  }

  if (characterPrivateData?.event) {
    return { canExplore: false, reason: 'ตัวละครกำลังอยู่ในเหตุการณ์' };
  }

  if (context.mapType === 'location' && context.usedVehicle) {
    const fuelLevel = characterPrivateData?.vehicleData?.states?.fuelLevel || 0;
    if (fuelLevel < 1) {
      return { canExplore: false, reason: 'น้ำมันไม่เพียงพอ' };
    }
  }

  return { canExplore: true, reason: '' };
}

/**
 * Composable สำหรับจัดการ state การสำรวจล่าสุด
 */
export function useQuickExplore() {
  const isExploring = ref(false);
  const error = ref(null);

  const context = ref(getLastExploreContext());

  const refreshContext = () => {
    context.value = getLastExploreContext();
  };

  const saveContext = (data) => {
    saveLastExploreContext(data);
    context.value = getLastExploreContext();
  };

  const clearContext = () => {
    clearLastExploreContext();
    context.value = null;
  };

  const explore = async (mapName, options = {}) => {
    isExploring.value = true;
    error.value = null;

    try {
      const result = await exploreMapByName(mapName, options);
      return result;
    } catch (err) {
      error.value = err.message || 'เกิดข้อผิดพลาดในการสำรวจ';
      throw err;
    } finally {
      isExploring.value = false;
    }
  };

  return {
    context,
    isExploring,
    error,
    refreshContext,
    saveContext,
    clearContext,
    explore
  };
}
